import { useCallback, useEffect, useState } from 'react'
import { Space, Typography, Tooltip, Spin } from 'antd'
import {
  ReloadOutlined,
  ClockCircleOutlined,
  PlusCircleOutlined
} from '@ant-design/icons'
import moment from 'moment'
import { useLazyQuery } from '@apollo/client'
import { getPayload } from '../../utils/auth'
import { WORKLOGS } from '../registroHoras/gql/verteOfficeGQL'

// Singleton: cualquier código puede llamar worklogBadge.refresh()
// sin importar en qué árbol React esté montado
export const worklogBadge = { refresh: () => {} }

interface WorklogStatusBadgeProps {
  onOpenModal: () => void
}

const ElapsedTimer: React.FC<{ startTime: string }> = ({ startTime }) => {
  const [elapsed, setElapsed] = useState('')

  useEffect(() => {
    const calc = () => {
      const diff = moment.duration(moment().diff(moment(startTime)))
      const h = Math.floor(diff.asHours())
      const m = diff.minutes()
      const s = diff.seconds()
      setElapsed(`${h}h ${m}m ${s}s`)
    }
    calc()
    const interval = setInterval(calc, 1000)
    return () => clearInterval(interval)
  }, [startTime])

  return (
    <Typography.Text strong style={{ fontSize: '0.85rem' }}>
      {elapsed}
    </Typography.Text>
  )
}

const WorklogStatusBadge: React.FC<WorklogStatusBadgeProps> = ({ onOpenModal }) => {
  const payload = getPayload()
  const [override, setOverride] = useState<null | { worklogs: any[] }>(null)

  const [fetchWorklogs, { data, loading }] = useLazyQuery(WORKLOGS, {
    fetchPolicy: 'no-cache'
  })

  const check = useCallback(() => {
    const worklogId = localStorage.getItem('current_worklog_id')
    if (worklogId) {
      setOverride(null)
      fetchWorklogs({
        variables: {
          where: {
            id: { equals: worklogId },
            user_id: { equals: payload?.id },
            deleted_at: { equals: null }
          },
          orderBy: [{ created_at: 'desc' }]
        }
      })
    } else {
      setOverride({ worklogs: [] })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Registra check en el singleton para que cualquier código externo pueda llamarlo
  worklogBadge.refresh = check

  // carga inicial
  useEffect(() => {
    check()
  }, [check])

  const effectiveData = override ?? data
  const active = effectiveData?.worklogs?.[0]
  const hasPending = active != null && active.end_time == null

  return (
    <Space
      size={4}
      align='center'
      style={{ display: 'flex', alignItems: 'center' }}
    >
      {loading ? (
        <Spin size='small' />
      ) : hasPending ? (
        <>
          <Tooltip title='Abrir actividad'>
            <ClockCircleOutlined
              onClick={onOpenModal}
              style={{
                fontSize: '0.9rem',
                cursor: 'pointer',
                color: '#52c41a'
              }}
            />
          </Tooltip>
          <ElapsedTimer startTime={active.start_time} />
        </>
      ) : (
        <Space
          size={4}
          align='center'
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <Typography.Text type='secondary' style={{ fontSize: '0.8rem' }}>
            Sin actividad
          </Typography.Text>
          <Tooltip title='Crear actividad'>
            <PlusCircleOutlined
              onClick={onOpenModal}
              style={{ fontSize: '0.9rem', cursor: 'pointer' }}
            />
          </Tooltip>
        </Space>
      )}
      <Tooltip title='Refrescar actividad' placement='bottom'>
        <ReloadOutlined
          onClick={check}
          style={{ fontSize: '0.75rem', cursor: 'pointer' }}
        />
      </Tooltip>
    </Space>
  )
}

export default WorklogStatusBadge
